import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { useEffect, useRef, useMemo } from "react";
import { applyDarkDefault } from "./applyDarkDefault";
import { useSnapshot } from "valtio";
import { stateMemory, stateInAreaRooms } from "../store";
import { disposeGLTF } from "../Utils/disposeGLTF";
import { assignMaterialToObjects } from "../Utils/AssignMaterialToObject";

const useSnap = () => {
  const mem = useSnapshot(stateMemory);
  const area = useSnapshot(stateInAreaRooms);
  return { mem, area };
};

const loadMaterial = ({ maps, lowDevice, props, done }) => {
  const loader = new THREE.TextureLoader();
  const base = "../overallTextures/Artefakte/";
  const path = lowDevice ? base + "LOW/" : base;

  if (!maps || maps.length === 0) {
    done(new THREE.MeshStandardMaterial({ ...props }));
    return;
  }

  const loaded = {};
  let remaining = maps.length;

  const resolveKey = (file) => {
    const f = file.toLowerCase();

    if (f.includes("normal")) return "normalMap";
    if (f.includes("rough")) return "roughnessMap";
    if (f.includes("metal")) return "metalnessMap";
    if (f.includes("ao")) return "aoMap";
    if (f.includes("alpha") || f.includes("opacity")) return "alphaMap";

    // fallback = color map
    return "map";
  };

  maps.forEach((file) => {
    const key = resolveKey(file);

    loader.load(path + file, (tex) => {
      if (key === "map") tex.colorSpace = THREE.SRGBColorSpace;
      tex.flipY = false;
      loaded[key] = tex;

      if (--remaining === 0) {
        const material = new THREE.MeshStandardMaterial({
          ...props,
          ...loaded,
        });
        material.needsUpdate = true;
        done(material);
      }
    });
  });
};

const artefact = ({ name, glbLow, glbHigh, maps, props }) => {
  return function Artefact() {
    const { mem, area } = useSnap();
    const mat = useRef();

    const gltf = useGLTF(glbHigh);

    // Dark placeholder synchronously before first render — no white flash.
    useMemo(() => applyDarkDefault(gltf.scene), [gltf.scene]);

    useEffect(() => {
      gltf.scene.name = name;
      gltf.scene.traverse((obj) => {
        if (obj.isMesh && obj.geometry?.attributes?.uv && !obj.geometry.attributes.uv2) {
          obj.geometry.setAttribute(
            "uv2",
            new THREE.BufferAttribute(obj.geometry.attributes.uv.array, 2)
          );
        }
      });
    }, []);

    useEffect(() => {
      loadMaterial({
        maps,
        lowDevice: mem.memoryLowDevice,
        props,
        done: (m) => {
          mat.current = m;
          gltf.scene.traverse((obj) => {
            if (obj.isMesh) {
              obj.material = m;
              obj.material.needsUpdate = true;
            }
          });
        },
      });
    }, [mem.memoryLowDevice]);

   useEffect(() => {
  return () => {
    mat.current?.dispose?.();
    disposeGLTF([gltf.scene]);
  };
}, [area.inAreaRoomEntryRoom]);

    const lowKey = name.charAt(0).toLowerCase() + name.slice(1) + "Low";
    const highKey = name.charAt(0).toLowerCase() + name.slice(1);

    return {
      [lowKey]: gltf,
      [highKey]: gltf,
    };
  };
};

export const AmmonitenArt = artefact({
  name: "AmmonitenArt",
  glbLow: "../models/Artefakte/AmmonitenLow.glb",
  glbHigh: "../models/Artefakte/Ammoniten.glb",
  maps: ['AmmonitenColor.webp', 'AmmonitenNormal.webp'],
  props: {'roughness': 0.5},
});

export const AntennendolchHaengend = artefact({
  name: "AntennendolchHaengend",
  glbLow: "../models/Artefakte/AntennendolchHaengendLow.glb",
  glbHigh: "../models/Artefakte/AntennendolchHaengend.glb",
  maps: ['AntennendolchHaengendColor.webp', 'AntennendolchHaengendNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const Arbeitsschuh = artefact({
  name: "Arbeitsschuh",
  glbLow: "../models/Artefakte/ArbeitsschuhLow.glb",
  glbHigh: "../models/Artefakte/Arbeitsschuh.glb",
  maps: ['ArbeitsschuhColor.webp', 'ArbeitsschuhNormal.webp'],
  props: {'roughness': 0.5},
});

export const Bergmannshut = artefact({
  name: "Bergmannshut",
  glbLow: "../models/Artefakte/BergmannshutLow.glb",
  glbHigh: "../models/Artefakte/Bergmannshut.glb",
  maps: ['BergmannshutColor.webp', 'BergmannshutNormal.webp'],
  props: {'roughness': 0.5},
});

export const Bergwerkschuh = artefact({
  name: "Bergwerkschuh",
  glbLow: "../models/Artefakte/BergwerkschuhLow.glb",
  glbHigh: "../models/Artefakte/Bergwerkschuh.glb",
  maps: ['BergwerkschuhColor.webp', 'BergwerkschuhNormal.webp'],
  props: {'roughness': 0.5},
});

export const BetbuchGeschlossen = artefact({
  name: "BetbuchGeschlossen",
  glbLow: "../models/Artefakte/BetbuchGeschlossenLow.glb",
  glbHigh: "../models/Artefakte/BetbuchGeschlossen.glb",
  maps: ['BetbuchGeschlossenColor.webp', 'BetbuchGeschlossenNormal.webp'],
  props: {'roughness': 0.5},
});

export const BetbuchOffen = artefact({
  name: "BetbuchOffen",
  glbLow: "../models/Artefakte/BetbuchOffenLow.glb",
  glbHigh: "../models/Artefakte/BetbuchOffen.glb",
  maps: ['BetbuchOffenColor.webp', 'BetbuchOffenNormal.webp'],
  props: {'roughness': 0.5},
});

export const Bohrer = artefact({
  name: "Bohrer",
  glbLow: "../models/Artefakte/BohrerLow.glb",
  glbHigh: "../models/Artefakte/Bohrer.glb",
  maps: ['BohrerColor.webp', 'BohrerNormal.webp'],
  props: {'roughness': 0.5},
});

export const Breithacke = artefact({
  name: "Breithacke",
  glbLow: "../models/Artefakte/BreithackeLow.glb",
  glbHigh: "../models/Artefakte/Breithacke.glb",
  maps: ['BreithackeColor.webp', 'BreithackeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Bronzehalsring = artefact({
  name: "Bronzehalsring",
  glbLow: "../models/Artefakte/BronzehalsringLow.glb",
  glbHigh: "../models/Artefakte/Bronzehalsring.glb",
  maps: ['BronzehalsringColor.webp', 'BronzehalsringNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.5},
});

export const Bronzehelm = artefact({
  name: "Bronzehelm",
  glbLow: "../models/Artefakte/BronzehelmLow.glb",
  glbHigh: "../models/Artefakte/Bronzehelm.glb",
  maps: ['BronzehelmColor.webp', 'BronzehelmNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const Bronzering = artefact({
  name: "Bronzering",
  glbLow: "../models/Artefakte/BronzeringLow.glb",
  glbHigh: "../models/Artefakte/Bronzering.glb",
  maps: ['BronzeringColor.webp', 'BronzeringNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const BuchLebenChristiGeschlossen = artefact({
  name: "BuchLebenChristiGeschlossen",
  glbLow: "../models/Artefakte/BuchLebenChristiGeschlossenLow.glb",
  glbHigh: "../models/Artefakte/BuchLebenChristiGeschlossen.glb",
  maps: ['BuchLebenChristiGeschlossenColor.webp', 'BuchLebenChristiGeschlossenNormal.webp'],
  props: {'roughness': 0.5},
});

export const BuchLebenChristiOffen = artefact({
  name: "BuchLebenChristiOffen",
  glbLow: "../models/Artefakte/BuchLebenChristiOffenLow.glb",
  glbHigh: "../models/Artefakte/BuchLebenChristiOffen.glb",
  maps: ['BuchLebenChristiOffenColor.webp', 'BuchLebenChristiOffenNormal.webp'],
  props: {'roughness': 0.5},
});

export const CeremonialAxe = artefact({
  name: "CeremonialAxe",
  glbLow: "../models/Artefakte/CeremonialAxeLow.glb",
  glbHigh: "../models/Artefakte/CeremonialAxe.glb",
  maps: ['CeremonialAxeColor.webp', 'CeremonialAxeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Dagger = artefact({
  name: "Dagger",
  glbLow: "../models/Artefakte/DaggerLow.glb",
  glbHigh: "../models/Artefakte/Dagger.glb",
  maps: ['DaggerColor.webp', 'DaggerNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const DaggerScheide = artefact({
  name: "DaggerScheide",
  glbLow: "../models/Artefakte/DaggerScheideLow.glb",
  glbHigh: "../models/Artefakte/DaggerScheide.glb",
  maps: ['DaggerscheideColor.webp', 'DaggerscheideNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const Deckel = artefact({
  name: "Deckel",
  glbLow: "../models/Artefakte/DeckelLow.glb",
  glbHigh: "../models/Artefakte/Deckel.glb",
  maps: ['DeckelColor.webp', 'DeckelNormale.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const EbenseeSoleleitung = artefact({
  name: "EbenseeSoleleitung",
  glbLow: "../models/Artefakte/EbenseeSoleleitungLow.glb",
  glbHigh: "../models/Artefakte/EbenseeSoleleitung.glb",
  maps: ['SoleleitungColor.webp', 'SoleleitungNormal.webp'],
  props: {'roughness': 0.5},
});

export const Elisabeth = artefact({
  name: "Elisabeth",
  glbLow: "../models/Artefakte/ElisabethLow.glb",
  glbHigh: "../models/Artefakte/Elisabeth.glb",
  maps: ['ElisabethColor.webp', 'ElisabethNormal.webp'],
  props: {'roughness': 0.35, 'metalness': 0.7},
});

export const Fellmuetze = artefact({
  name: "Fellmuetze",
  glbLow: "../models/Artefakte/FellmuetzeLow.glb",
  glbHigh: "../models/Artefakte/Fellmuetze.glb",
  maps: ['FellmuetzeColor.webp', 'FellmuetzeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Flinserl = artefact({
  name: "Flinserl",
  glbLow: "../models/Artefakte/FlinserlLow.glb",
  glbHigh: "../models/Artefakte/Flinserl.glb",
  maps: ['FlinserlColor.webp', 'FlinserlNormal.webp'],
  props: {'roughness': 0.5},
});

export const Floss = artefact({
  name: "Floss",
  glbLow: "../models/Artefakte/FlossLow.glb",
  glbHigh: "../models/Artefakte/Floss.glb",
  maps: ['FlossColor.webp', 'FlossNormal.webp'],
  props: {'roughness': 0.5},
});

export const FranzJoseph = artefact({
  name: "FranzJoseph",
  glbLow: "../models/Artefakte/FranzJosephLow.glb",
  glbHigh: "../models/Artefakte/FranzJoseph.glb",
  maps: ['FranzJosephColor.webp', 'FranzJosephNormal.webp'],
  props: {'roughness': 0.35, 'metalness': 0.7},
});

export const GrosserHalsring = artefact({
  name: "GrosserHalsring",
  glbLow: "../models/Artefakte/GrosserHalsringLow.glb",
  glbHigh: "../models/Artefakte/GrosserHalsring.glb",
  maps: ['GrosserhalsringColor.webp', 'GrosserhalsringNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const Grubenhund = artefact({
  name: "Grubenhund",
  glbLow: "../models/Artefakte/GrubenhundLow.glb",
  glbHigh: "../models/Artefakte/Grubenhund.glb",
  maps: ['GrubenhundColor.webp', 'GrubenhundNormal.webp'],
  props: {'roughness': 0.5},
});

export const Haeher = artefact({
  name: "Haeher",
  glbLow: "../models/Artefakte/HaeherLow.glb",
  glbHigh: "../models/Artefakte/Haeher.glb",
  maps: ['HaeherColor.webp', 'HaeherNormal.webp'],
  props: {'roughness': 0.5},
});

export const HandlederRechteckig = artefact({
  name: "HandlederRechteckig",
  glbLow: "../models/Artefakte/handlederRechteckigLow.glb",
  glbHigh: "../models/Artefakte/handlederRechteckig.glb",
  maps: ['HandlederRechteckigColor.webp', 'HandlederRechteckigNormal.webp'],
  props: {'roughness': 0.5},
});

export const Humpen = artefact({
  name: "Humpen",
  glbLow: "../models/Artefakte/HumpenLow.glb",
  glbHigh: "../models/Artefakte/Humpen.glb",
  maps: ['HumpenColor.webp', 'HumpenNormal.webp'],
  props: {'roughness': 0.2},
});

export const Jagdtasche = artefact({
  name: "Jagdtasche",
  glbLow: "../models/Artefakte/JagdtascheLow.glb",
  glbHigh: "../models/Artefakte/Jagdtasche.glb",
  maps: ['JagdtascheColor.webp', 'JagdtascheNormal.webp'],
  props: {'roughness': 0.5},
});

export const Kaiserschiff = artefact({
  name: "Kaiserschiff",
  glbLow: "../models/Artefakte/KaiserschiffLow.glb",
  glbHigh: "../models/Artefakte/Kaiserschiff.glb",
  maps: ['KaiserschiffColor.webp', 'KaiserschiffNormal.webp'],
  props: {'roughness': 0.5},
});

export const KarbitLampe = artefact({
  name: "KarbitLampe",
  glbLow: "../models/Artefakte/KarbitLampeLow.glb",
  glbHigh: "../models/Artefakte/KarbitLampe.glb",
  maps: ['KarbitlampeColor.webp', 'KarbitlampeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Kluppe = artefact({
  name: "Kluppe",
  glbLow: "../models/Artefakte/KluppeLow.glb",
  glbHigh: "../models/Artefakte/Kluppe.glb",
  maps: ['KluppeColor.webp', 'KluppeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Kompass = artefact({
  name: "Kompass",
  glbLow: "../models/Artefakte/KompassLow.glb",
  glbHigh: "../models/Artefakte/Kompass.glb",
  maps: ['KompassColor.webp', 'KompassNormal.webp'],
  props: {'roughness': 0.5},
});

export const Kratze = artefact({
  name: "Kratze",
  glbLow: "../models/Artefakte/KratzeLow.glb",
  glbHigh: "../models/Artefakte/Kratze.glb",
  maps: ['KratzeColor.webp', 'KratzeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Krauthobe = artefact({
  name: "Krauthobe",
  glbLow: "../models/Artefakte/KrauthobeLow.glb",
  glbHigh: "../models/Artefakte/Krauthobe.glb",
  maps: ['KrauthobeColor.webp', 'KrauthobeNormal.webp'],
  props: {'roughness': 0.5},
});

export const KroneSalzamtleiter = artefact({
  name: "KroneSalzamtleiter",
  glbLow: "../models/Artefakte/KroneSalzamtleiterLow.glb",
  glbHigh: "../models/Artefakte/KroneSalzamtleiter.glb",
  maps: ['KroneColor.webp', 'KroneNormal.webp'],
  props: {'roughness': 0.2},
});

export const Kuebel = artefact({
  name: "Kuebel",
  glbLow: "../models/Artefakte/KuebelLow.glb",
  glbHigh: "../models/Artefakte/Kuebel.glb",
  maps: ['KuebelColor.webp', 'KuebelNormal.webp'],
  props: {},
});

export const Kuhkaelbchenschale = artefact({
  name: "Kuhkaelbchenschale",
  glbLow: "../models/Artefakte/KuhkaelbchenschaleLow.glb",
  glbHigh: "../models/Artefakte/Kuhkaelbchenschale.glb",
  maps: ['KuhkaelbchenschaleColor.webp', 'KuhkaelbchenschaleNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const Lampe = artefact({
  name: "Lampe",
  glbLow: "../models/Artefakte/LampeLow.glb",
  glbHigh: "../models/Artefakte/Lampe.glb",
  maps: ['LampeColor.webp', 'LampeNormal.webp'],
  props: {'roughness': 0.2, 'metalness': 1.0},
});

export const Leuchtspanbuendel = artefact({
  name: "Leuchtspanbuendel",
  glbLow: "../models/Artefakte/LeuchtspanbuendelLow.glb",
  glbHigh: "../models/Artefakte/Leuchtspanbuendel.glb",
  maps: ['LeuchtspanbuendelColor.webp', 'LeuchtspanbuendelNormal.webp'],
  props: {'roughness': 0.5},
});

export const Miasteufel = artefact({
  name: "Miasteufel",
  glbLow: "../models/Artefakte/MiasteufelLow.glb",
  glbHigh: "../models/Artefakte/Miasteufel.glb",
  maps: ['MiasteufelColor.webp', 'MiasteufelNormal.webp'],
  props: {'roughness': 1.0},
});

export const ModelEiseneitThree = artefact({
  name: "ModelEiseneitThree",
  glbLow: "../models/Artefakte/ModelEiseneit3Low.glb",
  glbHigh: "../models/Artefakte/ModelEiseneit3.glb",
  maps: ['Eiseneit3Color.webp', 'Eiseneit3Normal.webp'],
  props: {'roughness': 0.5},
});

export const ModelEisenzeitTwo = artefact({
  name: "ModelEisenzeitTwo",
  glbLow: "../models/Artefakte/ModelEisenzeit2Low.glb",
  glbHigh: "../models/Artefakte/ModelEisenzeit2.glb",
  maps: ['Eisenzeitmodel2Color.webp', 'Eisenzeitmodel2Normal.webp'],
  props: {'roughness': 0.5},
});

export const ModelEiszeitOne = artefact({
  name: "ModelEiszeitOne",
  glbLow: "../models/Artefakte/ModelEiszeit1Low.glb",
  glbHigh: "../models/Artefakte/ModelEiszeit1.glb",
  maps: ['Eiszeit1Color.webp', 'Eiszeit1Normal.webp'],
  props: {'roughness': 0.5},
});

export const ModerenesGeleuchtKopflampe = artefact({
  name: "ModerenesGeleuchtKopflampe",
  glbLow: "../models/Artefakte/ModerenesGeleuchtKopflampeLow.glb",
  glbHigh: "../models/Artefakte/ModerenesGeleuchtKopflampe.glb",
  maps: ['GeleuchtkopflampeColor.webp', 'GeleuchtkopflampeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Papiermaschin = artefact({
  name: "Papiermaschin",
  glbLow: "../models/Artefakte/PapiermaschinLow.glb",
  glbHigh: "../models/Artefakte/Papiermaschin.glb",
  maps: ['PapiermaschinColor.webp', 'PapiermaschinNormal.webp'],
  props: {'roughness': 0.5},
});

export const Pfeifferl = artefact({
  name: "Pfeifferl",
  glbLow: "../models/Artefakte/PfeifferlLow.glb",
  glbHigh: "../models/Artefakte/Pfeifferl.glb",
  maps: ['PfeifferlColor.webp', 'PfeifferlNormal.webp'],
  props: {'roughness': 0.5},
});

export const PferdeeisenbahnReisegespann = artefact({
  name: "PferdeeisenbahnReisegespann",
  glbLow: "../models/Artefakte/PferdeeisenbahnReisegespannLow.glb",
  glbHigh: "../models/Artefakte/PferdeeisenbahnReisegespann.glb",
  maps: ['PferdeeisenbahnReisegespannColor.webp', 'PferdeeisenbahnReisegespannNormal.webp'],
  props: {'roughness': 0.5},
});

export const PferdeeisenbahnTransportgespann = artefact({
  name: "PferdeeisenbahnTransportgespann",
  glbLow: "../models/Artefakte/PferdeeisenbahnTransportgespannLow.glb",
  glbHigh: "../models/Artefakte/PferdeeisenbahnTransportgespann.glb",
  maps: ['PferdeeisenbahnTransportgespannColor.webp', 'PferdeeisenbahnTransportgespannNormal.webp'],
  props: {'roughness': 0.5},
});

export const PuppeOne = artefact({
  name: "PuppeOne",
  glbLow: "../models/Artefakte/Puppe1Low.glb",
  glbHigh: "../models/Artefakte/Puppe1.glb",
  maps: ['Puppe1Color.webp', 'Puppe1Normal.webp'],
  props: {'roughness': 0.5},
});

export const PuppeTwo = artefact({
  name: "PuppeTwo",
  glbLow: "../models/Artefakte/Puppe2Low.glb",
  glbHigh: "../models/Artefakte/Puppe2.glb",
  maps: ['Puppe2Color.webp', 'Puppe2Normal.webp'],
  props: {'roughness': 0.5},
});

export const PuppeThree = artefact({
  name: "PuppeThree",
  glbLow: "../models/Artefakte/Puppe3Low.glb",
  glbHigh: "../models/Artefakte/Puppe3.glb",
  maps: ['Puppe3Color.webp', 'Puppe3Normal.webp'],
  props: {'roughness': 0.5},
});

export const ReformationslibellOffen = artefact({
  name: "ReformationslibellOffen",
  glbLow: "../models/Artefakte/ReformationslibellOffenLow.glb",
  glbHigh: "../models/Artefakte/ReformationslibellOffen.glb",
  maps: ['ReformationslibellOffenColor.webp', 'ReformationslibellOffenNormal.webp'],
  props: {'roughness': 0.5},
});

export const Riedl = artefact({
  name: "Riedl",
  glbLow: "../models/Artefakte/RiedlLow.glb",
  glbHigh: "../models/Artefakte/Riedl.glb",
  maps: ['RiedlColor.webp', 'RiedlNormal.webp'],
  props: {'roughness': 0.5},
});

export const Ringgriffmesser = artefact({
  name: "Ringgriffmesser",
  glbLow: "../models/Artefakte/RinggriffmesserLow.glb",
  glbHigh: "../models/Artefakte/Ringgriffmesser.glb",
  maps: ['RinggriffmesserColor.webp', 'RinggriffmesserNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const RotesFasersalz = artefact({
  name: "RotesFasersalz",
  glbLow: "../models/Artefakte/RotesFasersalzLow.glb",
  glbHigh: "../models/Artefakte/RotesFasersalz.glb",
  maps: ['RotesFasersalzColor.webp', 'RotesFasersalzNormal.webp'],
  props: {'roughness': 0.5},
});

export const Saege = artefact({
  name: "Saege",
  glbLow: "../models/Artefakte/SaegeLow.glb",
  glbHigh: "../models/Artefakte/Saege.glb",
  maps: ['SaegeColor.webp', 'SaegeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Salzfabrik = artefact({
  name: "Salzfabrik",
  glbLow: "../models/Artefakte/SalzfabrikLow.glb",
  glbHigh: "../models/Artefakte/Salzfabrik.glb",
  maps: ['SalzfabrikColor.webp', 'SalzfabrikNormal.webp'],
  props: {'roughness': 0.5},
});

export const Salzkuefel = artefact({
  name: "Salzkuefel",
  glbLow: "../models/Artefakte/SalzkuefelLow.glb",
  glbHigh: "../models/Artefakte/Salzkuefel.glb",
  maps: ['SalzkuefelColor.webp', 'SalzkuefelNormal.webp'],
  props: {'roughness': 0.5},
});

export const SalzkuefelTwo = artefact({
  name: "SalzkuefelTwo",
  glbLow: "../models/Artefakte/Salzkuefel2Low.glb",
  glbHigh: "../models/Artefakte/Salzkuefel2.glb",
  maps: ['Salzkuefel2Color.webp', 'Salzkuefel2Normal.webp'],
  props: {'roughness': 0.5},
});

export const Salzschaufel = artefact({
  name: "Salzschaufel",
  glbLow: "../models/Artefakte/SalzschaufelLow.glb",
  glbHigh: "../models/Artefakte/Salzschaufel.glb",
  maps: ['SalzschaufelColor.webp', 'SalzschaufelNormal.webp'],
  props: {'roughness': 0.5},
});

export const Salzstreuer = artefact({
  name: "Salzstreuer",
  glbLow: "../models/Artefakte/SalzstreuerLow.glb",
  glbHigh: "../models/Artefakte/Salzstreuer.glb",
  maps: ['SalzstreuerColor.webp', 'SalzstreuerNormal.webp'],
  props: {'roughness': 0.2},
});

export const Salzverpackung = artefact({
  name: "Salzverpackung",
  glbLow: "../models/Artefakte/SalzverpackungLow.glb",
  glbHigh: "../models/Artefakte/Salzverpackung.glb",
  maps: ['SalzverpackungColor.webp', 'SalzverpackungNormal.webp'],
  props: {'roughness': 0.5},
});

export const Sappl = artefact({
  name: "Sappl",
  glbLow: "../models/Artefakte/SapplLow.glb",
  glbHigh: "../models/Artefakte/Sappl.glb",
  maps: ['SapplColor.webp', 'SapplNormal.webp'],
  props: {'roughness': 0.5},
});

export const Schale = artefact({
  name: "Schale",
  glbLow: "../models/Artefakte/SchaleLow.glb",
  glbHigh: "../models/Artefakte/Schale.glb",
  maps: ['SchaleColor.webp', 'SchaleNormal.webp'],
  props: {'roughness': 0.5},
});

export const Schaufel = artefact({
  name: "Schaufel",
  glbLow: "../models/Artefakte/SchaufelLow.glb",
  glbHigh: "../models/Artefakte/Schaufel.glb",
  maps: ['SchaufelColor.webp', 'SchaufelNormal.webp'],
  props: {'roughness': 0.5},
});

export const Schiffskasse = artefact({
  name: "Schiffskasse",
  glbLow: "../models/Artefakte/SchiffskasseLow.glb",
  glbHigh: "../models/Artefakte/Schiffskasse.glb",
  maps: ['SchiffskasseColor.webp', 'SchiffskasseNormal.webp'],
  props: {'roughness': 0.5},
});

export const Schmalzdose = artefact({
  name: "Schmalzdose",
  glbLow: "../models/Artefakte/SchmalzdoseLow.glb",
  glbHigh: "../models/Artefakte/Schmalzdose.glb",
  maps: ['SchmalzdoseColor.webp', 'SchmalzdoseNormal.webp'],
  props: {'roughness': 0.5},
});

export const SchuesselArchekult = artefact({
  name: "SchuesselArchekult",
  glbLow: "../models/Artefakte/SchuesselArchekultLow.glb",
  glbHigh: "../models/Artefakte/SchuesselArchekult.glb",
  maps: ['SchuesselarchekultColor.webp', 'SchuesselarchekultNormal.webp'],
  props: {'roughness': 0.5},
});

export const Schwertknauf = artefact({
  name: "Schwertknauf",
  glbLow: "../models/Artefakte/SchwertknaufLow.glb",
  glbHigh: "../models/Artefakte/Schwertknauf.glb",
  maps: ['SchwertknaufColor.webp', 'SchwertknaufNormal.webp'],
  props: {'roughness': 0.5},
});

export const SignalHorn = artefact({
  name: "SignalHorn",
  glbLow: "../models/Artefakte/SignalHornLow.glb",
  glbHigh: "../models/Artefakte/SignalHorn.glb",
  maps: ['SignalhornColor.webp', 'SignalhornNormal.webp'],
  props: {'roughness': 0.5},
});

export const Solepfanne = artefact({
  name: "Solepfanne",
  glbLow: "../models/Artefakte/SolepfanneLow.glb",
  glbHigh: "../models/Artefakte/Solepfanne.glb",
  maps: ['SolepfanneColor.webp', 'SolepfanneNormal.webp'],
  props: {'roughness': 0.5},
});

export const Spanschachtel = artefact({
  name: "Spanschachtel",
  glbLow: "../models/Artefakte/SpanschachtelLow.glb",
  glbHigh: "../models/Artefakte/Spanschachtel.glb",
  maps: ['SpanschachtelColor.webp', 'SpanschachtelNormal.webp'],
  props: {'roughness': 0.5},
});

export const Spiralanstecker = artefact({
  name: "Spiralanstecker",
  glbLow: "../models/Artefakte/SpiralansteckerLow.glb",
  glbHigh: "../models/Artefakte/Spiralanstecker.glb",
  maps: ['SpiralansteckerColor.webp', 'SpiralansteckerNormal.webp'],
  props: {'roughness': 0.5},
});

export const StadlPauraSalzkuefel = artefact({
  name: "StadlPauraSalzkuefel",
  glbLow: "../models/Artefakte/StadlPauraSalzkuefelLow.glb",
  glbHigh: "../models/Artefakte/StadlPauraSalzkuefel.glb",
  maps: ['StadlPauraSalzkuefelColor.webp', 'StadlPauraSalzkuefelNormal.webp'],
  props: {'roughness': 0.5},
});

export const Steinbeil = artefact({
  name: "Steinbeil",
  glbLow: "../models/Artefakte/SteinbeilLow.glb",
  glbHigh: "../models/Artefakte/Steinbeil.glb",
  maps: ['SteinbeilColor.webp', 'SteinbeilNormal.webp'],
  props: {'roughness': 0.5},
});

export const Steinsalzdruse = artefact({
  name: "Steinsalzdruse",
  glbLow: "../models/Artefakte/SteinsalzdruseLow.glb",
  glbHigh: "../models/Artefakte/Steinsalzdruse.glb",
  maps: ['SteinsalzdruseColor.webp', 'SteinsalzdruseNormal.webp'],
  props: {'roughness': 0.5},
});

export const Stier = artefact({
  name: "Stier",
  glbLow: "../models/Artefakte/StierLow.glb",
  glbHigh: "../models/Artefakte/Stier.glb",
  maps: ['StierColor.webp', 'StierNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const Stierhornschenkelschale = artefact({
  name: "Stierhornschenkelschale",
  glbLow: "../models/Artefakte/StierhornschenkelschaleLow.glb",
  glbHigh: "../models/Artefakte/Stierhornschenkelschale.glb",
  maps: ['StierhornschenkelschaleColor.webp', 'StierhornschenkelschaleNormal.webp'],
  props: {'roughness': 0.5},
});

export const Stollenschachtmodell = artefact({
  name: "Stollenschachtmodell",
  glbLow: "../models/Artefakte/StollenschachtmodellLow.glb",
  glbHigh: "../models/Artefakte/Stollenschachtmodell.glb",
  maps: ['StollenschachtmodellColor.webp', 'StollenschachtmodellNormal.webp'],
  props: {'roughness': 0.5},
});

export const Sylvinit = artefact({
  name: "Sylvinit",
  glbLow: "../models/Artefakte/SylvinitLow.glb",
  glbHigh: "../models/Artefakte/Sylvinit.glb",
  maps: ['SylvinitColor.webp', 'SylvinitNormal.webp'],
  props: {'roughness': 0.5},
});

export const Talglampe = artefact({
  name: "Talglampe",
  glbLow: "../models/Artefakte/TalglampeLow.glb",
  glbHigh: "../models/Artefakte/Talglampe.glb",
  maps: ['TalglampeColor.webp', 'TalglampeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Tonwuerfelsalz = artefact({
  name: "Tonwuerfelsalz",
  glbLow: "../models/Artefakte/TonwuerfelsalzLow.glb",
  glbHigh: "../models/Artefakte/Tonwuerfelsalz.glb",
  maps: ['TonwuerfelsalzColor.webp', 'TonwuerfelsalzNormal.webp'],
  props: {'roughness': 0.5},
});

export const Tracht = artefact({
  name: "Tracht",
  glbLow: "../models/Artefakte/TrachtLow.glb",
  glbHigh: "../models/Artefakte/Tracht.glb",
  maps: ['TrachtColor.webp', 'TrachtNormal.webp'],
  props: {'roughness': 0.5},
});

export const Trauner = artefact({
  name: "Trauner",
  glbLow: "../models/Artefakte/TraunerLow.glb",
  glbHigh: "../models/Artefakte/Trauner.glb",
  maps: ['TraunerColor.webp', 'TraunerNormal.webp'],
  props: {'roughness': 0.5},
});

export const Traunfall = artefact({
  name: "Traunfall",
  glbLow: "../models/Artefakte/TraunfallLow.glb",
  glbHigh: "../models/Artefakte/Traunfall.glb",
  maps: ['TraunfallColor.webp', 'TraunfallNormal.webp'],
  props: {'roughness': 0.5},
});

export const Trommelweibertrommel = artefact({
  name: "Trommelweibertrommel",
  glbLow: "../models/Artefakte/TrommelweibertrommelLow.glb",
  glbHigh: "../models/Artefakte/Trommelweibertrommel.glb",
  maps: ['TrommelweibertrommelColor.webp', 'TrommelweibertrommelNormal.webp'],
  props: {'roughness': 0.5},
});

export const Wasserlagel = artefact({
  name: "Wasserlagel",
  glbLow: "../models/Artefakte/WasserlagelLow.glb",
  glbHigh: "../models/Artefakte/Wasserlagel.glb",
  maps: ['WasserlagelColor.webp', 'WasserlagelNormal.webp'],
  props: {'roughness': 0.5},
});

export const Zellstoffballen = artefact({
  name: "Zellstoffballen",
  glbLow: "../models/Artefakte/ZellstoffballenLow.glb",
  glbHigh: "../models/Artefakte/Zellstoffballen.glb",
  maps: ['ZellstoffballenColor.webp', 'ZellstoffballenNormal.webp'],
  props: {'roughness': 0.5},
});

export const Zille = artefact({
  name: "Zille",
  glbLow: "../models/Artefakte/ZilleLow.glb",
  glbHigh: "../models/Artefakte/Zille.glb",
  maps: ['ZilleColor.webp', 'ZilleNormal.webp'],
  props: {'roughness': 0.5},
});

export const Zunfttruhe = artefact({
  name: "Zunfttruhe",
  glbLow: "../models/Artefakte/ZunfttruheLow.glb",
  glbHigh: "../models/Artefakte/Zunfttruhe.glb",
  maps: ['ZunfttruheColor.webp', 'ZunfttruheNormal.webp'],
  props: {'roughness': 0.5},
});

export const BronzezeitPickel = artefact({
  name: "BronzezeitPickel",
  glbLow: "../models/Museum01/Salzabbau/Werkzeuge/bronzezeitPickelLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/Werkzeuge/bronzezeitPickel.glb",
  maps: ['bronzezeitPickelColor.webp', 'bronzezeitPickelNormal.webp', 'bronzezeitPickelMetal.webp', 'bronzezeitPickelRough.webp'],
  props: {},
});

export const Tragesack = artefact({
  name: "Tragesack",
  glbLow: "../models/Artefakte/TragesackLow.glb",
  glbHigh: "../models/Artefakte/Tragesack.glb",
  maps: ['TragesackColorMap_.webp', 'TragesackNormalMap_.webp'],
  props: {},
});

export const EisenzeitPickel = artefact({
  name: "EisenzeitPickel",
  glbLow: "../models/Museum01/Salzabbau/Werkzeuge/eisenzeitPickelLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/Werkzeuge/eisenzeitPickel.glb",
  maps: ['esienzeitPickelRough.webp', 'esienzeitPickelColor.webp', 'esienzeitPickelNormal.webp', 'esienzeitPickelMetal.webp'],
  props: {},
});

export const Bergeisen = artefact({
  name: "Bergeisen",
  glbLow: "../models/Museum01/Salzabbau/Werkzeuge/mittelalterBergeisenLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/Werkzeuge/mittelalterBergeisen.glb",
  maps: ['BergeisenColor.webp', 'BergeisemNormal.webp', 'BergeisenMetal.webp', 'BergeisemRough.webp'],
  props: {},
});

export const PressLufthammer = artefact({
  name: "PressLufthammer",
  glbLow: "../models/Museum01/Salzabbau/Werkzeuge/pressLuftHammerLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/Werkzeuge/pressLuftHammer.glb",
  maps: ['pressluftColor.webp', 'pressluftNormal.webp', 'pressluftMetal.webp', 'pressluftRough.webp'],
  props: {},
});

export const SteinzeitHirschhaue = artefact({
  name: "SteinzeitHirschhaue",
  glbLow: "../models/Museum01/Salzabbau/Werkzeuge/steinzeitHirschhaueLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/Werkzeuge/steinzeitHirschhaue.glb",
  maps: ['hischPickelColor.webp', 'hischPickelNormal.webp'],
  props: {},
});

export const Bohrkopf = artefact({
  name: "Bohrkopf",
  glbLow: "../models/Museum01/Salzabbau/Werkzeuge/BohrkopfLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/Werkzeuge/Bohrkopf.glb",
  maps: ['bohrkopfColor.webp', 'bohrkopfNormal.webp', 'bohrkopfMetal.webp', 'bohrkopfRough.webp', 'bohrkopfAO.webp'],
  props: {'metalness': 1.0},
});

export const Schlegel = artefact({
  name: "Schlegel",
  glbLow: "../models/Museum01/Salzabbau/Werkzeuge/SchlegelLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/Werkzeuge/Schlegel.glb",
  maps: ['SchlegelEisenColor.webp', 'SchlegelEisenAlpha.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const BrillenfinelArt = artefact({
  name: "BrillenfinelArt",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/FgrabBrillenfibelLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/FgrabBrillenfibel.glb",
  maps: ['BrillenfibelColor.webp', 'BrillenfibelNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const BronzeArmbandArt = artefact({
  name: "BronzeArmbandArt",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/FgrabBronzeArmbandLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/FgrabBronzeArmband.glb",
  maps: ['BronzeArmringColor.webp', 'BronzeArmringNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const EisenbeilHolzstueck = artefact({
  name: "EisenbeilHolzstueck",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabEisenbeilHolzstueckLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabEisenbeilHolzstueck.glb",
  maps: ['EisenbeilHolzstueckColor.webp', 'EisenbeilHolzstueckNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const LanzenspitzeArt = artefact({
  name: "LanzenspitzeArt",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabLanzenspitzeLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabLanzenspitze.glb",
  maps: ['LanzenspitzeColor.webp', 'LanzenspitzeNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const SitulaArt = artefact({
  name: "SitulaArt",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabSitulaLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabSitula.glb",
  maps: ['SitulaColor.webp', 'SitulaNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const SpeerspitzeArt = artefact({
  name: "SpeerspitzeArt",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabSpeerSpitzeLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabSpeerSpitze.glb",
  maps: ['EiserneSpeerspitzeColor.webp', 'EiserneSpeerspitzeNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const TullenbeilArt = artefact({
  name: "TullenbeilArt",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabTullenbeilLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/MgrabTullenbeil.glb",
  maps: ['TuellenbeilColor.webp', 'TuellenbeilNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const EisenSchwert = artefact({
  name: "EisenSchwert",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/EisenschwertLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/Eisenschwert.glb",
  maps: ['EisenschwertColor.webp', 'EisenschwertNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 1.0},
});

export const Mehrkopfnadel = artefact({
  name: "Mehrkopfnadel",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/MehrkopfnadelLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/Mehrkopfnadel.glb",
  maps: ['MehrkopfnadelColor.webp', 'MehrkopfnadelNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const Breitrandschale = artefact({
  name: "Breitrandschale",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/BreitrandschaleLow.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/Breitrandschale.glb",
  maps: ['BreitbandschaleColor.webp', 'BreitbandschaleNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const BreitrandschaleMitVoeglchen = artefact({
  name: "BreitrandschaleMitVoeglchen",
  glbLow: "../models/Museum01/Salzabbau/GrabArtefakte/BreitrandschaleMitVoegelchenLOW.glb",
  glbHigh: "../models/Museum01/Salzabbau/GrabArtefakte/BreitrandschaleMitVoegelchen.glb",
  maps: ['BreitbandschaleColor.webp', 'BreitbandschaleNormal.webp'],
  props: {'roughness': 0.3, 'metalness': 0.8},
});

export const GmundenSohleleitung = artefact({
  name: "GmundenSohleleitung",
  glbLow: "../models/Artefakte/GmundenSohleleitungLow.glb",
  glbHigh: "../models/Artefakte/GmundenSohleleitung.glb",
  maps: ['GmundenSohleleitungColor.webp', 'GmundenSohleleitungNormal.webp'],
  props: {'roughness': 0.5},
});

export const Barbara = artefact({
  name: "Barbara",
  glbLow: "../models/Artefakte/Barbara.glb",
  glbHigh: "../models/Artefakte/BarbaraLow.glb",
  maps: ['BarbaraColor.webp', 'BarbaraNormal.webp'],
  props: {'roughness': 0.5},
});

export const OriginalStiege = artefact({
  name: "OriginalStiege",
  glbLow: "../models/Artefakte/OriginalStiege.glb",
  glbHigh: "../models/Artefakte/OriginalStiegeLow.glb",
  maps: ['OriginalstiegeColor.webp', 'OriginalstiegeNormal.webp'],
  props: {'roughness': 0.5},
});

export const Salzschiff = artefact({
  name: "Salzschiff",
  glbLow: "../models/Museum01/Handelswege/Boot.glb",
  glbHigh: "../models/Museum01/Handelswege/Boot.glb",
  maps: ['BootColor.webp', 'BootNormal.webp', 'BootAO.webp'],
  props: {'roughness': 0.5},
});


